import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertTriangle, Loader2, Truck } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import {
  TrailerCrossTenantDialog,
  findTrailerInOtherTenants,
  type OtherTenantTrailerMatch,
} from "@/components/trailers/TrailerCrossTenantDialog";

interface DuplicateRow {
  trailer_id_text: string;
  tenant_count: number;
  tenant_slugs: string[];
}

export function TrailerHealthCard() {
  const { tenantId, activeTenant } = useTenant();
  const [activeTrailer, setActiveTrailer] = useState<{
    trailerId: string;
    matches: OtherTenantTrailerMatch[];
  } | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trailer-cross-tenant-duplicates"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "list_cross_tenant_trailer_duplicates" as any,
      );
      if (error) throw error;
      return (data as DuplicateRow[] | null) ?? [];
    },
  });

  const duplicates = useMemo(() => data ?? [], [data]);

  const handleResolve = async (trailerIdText: string) => {
    if (!tenantId) return;
    const matches = await findTrailerInOtherTenants(trailerIdText, tenantId);
    if (matches.length === 0) {
      // Already cleaned up — refresh list.
      void refetch();
      return;
    }
    setActiveTrailer({ trailerId: trailerIdText, matches });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Trailer Health
          </CardTitle>
          <CardDescription>
            Trailers whose identifier exists in more than one environment. Resolve drift by
            migrating each trailer into its correct tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scanning trailers...
            </div>
          ) : duplicates.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No cross-tenant trailer duplicates detected. Nice.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trailer</TableHead>
                  <TableHead>Tenants</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {duplicates.map((row) => (
                  <TableRow key={row.trailer_id_text}>
                    <TableCell className="font-mono">{row.trailer_id_text}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.tenant_slugs.map((slug) => (
                          <Badge
                            key={slug}
                            variant={slug === activeTenant ? "default" : "outline"}
                            className="text-xs"
                          >
                            {slug}
                          </Badge>
                        ))}
                        {row.tenant_count > row.tenant_slugs.length && (
                          <Badge variant="secondary" className="text-xs">
                            +{row.tenant_count - row.tenant_slugs.length}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResolve(row.trailer_id_text)}
                      >
                        <AlertTriangle className="mr-2 h-3.5 w-3.5" />
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {activeTrailer && (
        <TrailerCrossTenantDialog
          open={!!activeTrailer}
          onOpenChange={(open) => {
            if (!open) setActiveTrailer(null);
          }}
          trailerIdText={activeTrailer.trailerId}
          matches={activeTrailer.matches}
          destinationLocationId={null}
          destinationStatus="unknown"
          onMigrated={() => {
            void refetch();
          }}
        />
      )}
    </>
  );
}